import React, { useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Typography,
} from '@mui/material';
import {
  Download as DownloadIcon,
  TableChart as TableChartIcon,
  Code as CodeIcon,
  PictureAsPdf as PictureAsPdfIcon,
} from '@mui/icons-material';
import { format as formatDate } from 'date-fns';
import { ExportFormat } from '../../../types/notification-admin';

interface ExportControlsProps {
  data: any[];
  filename?: string;
  onExport?: (format: ExportFormat) => void;
  disabled?: boolean;
}

/**
 * ExportControls - Export data to various formats (CSV, JSON, PDF)
 */
const ExportControls: React.FC<ExportControlsProps> = ({
  data,
  filename = 'notification-data',
  onExport,
  disabled = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [exporting, setExporting] = useState(false);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExport = async (format: ExportFormat) => {
    handleClose();

    if (onExport) {
      onExport(format);
      return;
    }

    setExporting(true);
    try {
      const timestamp = formatDate(new Date(), 'yyyy-MM-dd-HHmmss');
      const filenameWithTimestamp = `${filename}-${timestamp}`;

      switch (format) {
        case 'csv':
          exportToCSV(data, filenameWithTimestamp);
          break;
        case 'json':
          exportToJSON(data, filenameWithTimestamp);
          break;
        case 'pdf':
          // PDF export would require a library like jsPDF
          alert('PDF export functionality coming soon');
          break;
      }
    } finally {
      setExporting(false);
    }
  };

  const exportToCSV = (data: any[], filename: string) => {
    if (!data || data.length === 0) {
      alert('No data to export');
      return;
    }

    // Get headers from first object
    const headers = Object.keys(data[0]);

    // Create CSV content
    const csvContent = [
      headers.join(','), // Header row
      ...data.map((row) =>
        headers
          .map((header) => {
            const value = row[header];
            // Handle dates
            if (value instanceof Date) {
              return formatDate(value, 'yyyy-MM-dd HH:mm:ss');
            }
            // Handle strings with commas or quotes
            if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
              return `"${value.replace(/"/g, '""')}"`;
            }
            return value ?? '';
          })
          .join(',')
      ),
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToJSON = (data: any[], filename: string) => {
    if (!data || data.length === 0) {
      alert('No data to export');
      return;
    }

    // Convert dates to ISO strings
    const processedData = data.map((item) => {
      const processed: any = {};
      Object.keys(item).forEach((key) => {
        if (item[key] instanceof Date) {
          processed[key] = item[key].toISOString();
        } else {
          processed[key] = item[key];
        }
      });
      return processed;
    });

    // Create JSON content with pretty formatting
    const jsonContent = JSON.stringify(processedData, null, 2);

    // Create and download file
    const blob = new Blob([jsonContent], { type: 'application/json' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.json`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const menuItems = [
    {
      format: 'csv' as ExportFormat,
      label: 'Export as CSV',
      icon: <TableChartIcon />,
      description: 'Comma-separated values for Excel',
    },
    {
      format: 'json' as ExportFormat,
      label: 'Export as JSON',
      icon: <CodeIcon />,
      description: 'JSON format for developers',
    },
    {
      format: 'pdf' as ExportFormat,
      label: 'Export as PDF',
      icon: <PictureAsPdfIcon />,
      description: 'Printable PDF report',
      disabled: true, // Coming soon
    },
  ];

  return (
    <Box>
      <Button
        variant="outlined"
        startIcon={exporting ? <CircularProgress size={16} /> : <DownloadIcon />}
        onClick={handleClick}
        disabled={disabled || exporting || !data || data.length === 0}
      >
        {exporting ? 'Exporting...' : 'Export Data'}
      </Button>

      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {menuItems.map((item) => (
          <MenuItem
            key={item.format}
            onClick={() => handleExport(item.format)}
            disabled={item.disabled}
          >
            <ListItemIcon>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.label}
              secondary={item.description}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
          </MenuItem>
        ))}
      </Menu>

      {(!data || data.length === 0) && !disabled && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          No data available to export
        </Typography>
      )}
    </Box>
  );
};

export default ExportControls;
